import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  REQUEST_VALIDATION_TO_ROLE,
  ROLES_ALLOWED_TO_REQUEST_VALIDATION,
  ValidationRequestService,
} from '@app/features/contracts/validation/validation-request-service';
import { ValidationRequestType } from '@app/features/contracts/validation/validation-request.model';
import { UserMenu } from '@app/features/users/user-menu/user-menu';
import { User, UserRole } from '@app/features/users/user.model';
import { HasRoleDirective } from '@app/shared/auth/has-role.directive';
import {
  CommentDialog,
  CommentDialogData,
} from '@app/shared/dialogs/comment-dialog/comment-dialog';
import { ValidationCircuits } from '@app/features/contracts/validation/validation-circuit';
import { ValidationSubjectItem } from '@app/features/contracts/validation/validation-subject.model';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { filter, switchMap, tap } from 'rxjs';
import { ValidationRequestTypeLabelPipe } from '../validation-request-type-label-pipe';

@Component({
  selector: 'app-button-request-validation',
  imports: [
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    UserMenu,
    HasRoleDirective,
    MatTooltipModule,
    ValidationRequestTypeLabelPipe,
  ],
  templateUrl: './button-request-validation.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./button-request-validation.scss'],
})
export class ButtonRequestValidation extends RxjsDirective {
  /** Le sujet validé : un contrat d'embauche, ou un avenant. */
  contract = input.required<ValidationSubjectItem>();
  private readonly validationCircuits = inject(ValidationCircuits);
  protected circuit = () => this.validationCircuits.of(this.contract());
  validationType = input.required<ValidationRequestType>();
  validationRequested = output<void>();

  cdr = inject(ChangeDetectorRef);
  dialog = inject(MatDialog);
  validationRequestService = inject(ValidationRequestService);

  rolesAllowedToRequestValidation: UserRole[] = [];
  requestToRoles: UserRole[] = [];

  constructor() {
    super();
    effect(() => {
      const validationType = this.validationType();
      this.rolesAllowedToRequestValidation = ROLES_ALLOWED_TO_REQUEST_VALIDATION[validationType];
      this.requestToRoles = REQUEST_VALIDATION_TO_ROLE[validationType];
      this.cdr.markForCheck();
    });
  }

  get canRequestValidation(): boolean {
    return (
      this.validationRequestService.canRequestValidationForState(
        this.contract(),
        this.validationType(),
      ) &&
      this.requestToRoles.length > 0 &&
      this.rolesAllowedToRequestValidation.length > 0
    );
  }

  requestValidation(user: User) {
    this.register(
      this.circuit()
        .verify(this.contract().id)
        .pipe(
          switchMap(() => {
            const dialogRef = this.dialog.open<
              CommentDialog,
              CommentDialogData,
              string | undefined
            >(CommentDialog, {
              data: {
                title: 'Demande de validation',
                required: false,
              },
              minWidth: '700px',
            });
            return dialogRef.afterClosed();
          }),
          filter((comment: string | undefined): comment is string => comment !== undefined),
          switchMap((comment: string) =>
            this.circuit().request(this.contract().id, user.id, this.validationType(), comment),
          ),
          tap(() => this.validationRequested.emit()),
        )
        .subscribe(),
    );
  }
}
